
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { handleError, initClient } from '../../api';
import { Group, GroupUiClient, ListItem } from '../backend/backendapi';
import EditModal from './Editmodal';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { emptyUUID, formatDateTimeSeconds, WindowType } from '../../util';
import GroupStore from '../../stores/GroupStore';
import { paginationOptions, searchOptions } from '../../util/vue-good-table';
import { VgtRemoteManger } from '../../util/VgtRemoteManger';
import { applyGroupSelector, GroupSelector } from './util';

declare var window: WindowType;

@Component({
  components: {
    EditModal,
    VueGoodTable,
  },
})
export default class Groups<T> extends Vue {
  @Prop({ required: true })
  readonly feature!: string;
  @Prop({ default: null })
  readonly groups!: T[] | null;
  @Prop({ default: '' })
  readonly groupSelector!: GroupSelector<T>;
  @Prop({ default: () => (g: Group) => Group.fromJS(g) })
  readonly groupCopyFunction!: ((t: T) => T);
  @Prop({ default: null })
  readonly groupInitFunction!: (() => T) | null;
  @Prop({ default: null })
  readonly groupSaveFunction!: ((t: T) => Promise<T>) | null;
  @Prop({ default: null })
  readonly groupDeleteFunction!: ((t: T) => Promise<void>) | null;
  @Prop({ default: true })
  readonly canDeleteGroups!: boolean;
  @Prop({ default: false })
  readonly showPinnedGroupsOnly!: boolean;
  @Prop({ default: false })
  readonly allowNoEmailForExternalUsers!: boolean;
  @Prop({ default: null })
  readonly vgtManager!: VgtRemoteManger<T> | null;
  @Prop({ default: null })
  readonly vgtColumnModifier!: ((columns: unknown[]) => unknown[]) | null;

  //data
  readonly groupStore = new GroupStore();
  readonly users: ListItem[] = [];

  //api
  private readonly api = initClient(GroupUiClient);

  get availableSalutations(): string[] {
    return [...new Set([...this.groupsForFeature.map(e => applyGroupSelector(e, this.groupSelector))]
        .flatMap(group => group.externalUsers ?? [])
        .map(e => e.salutation)
        .filter(e => e?.trim().length)
        .map(e => e!),
    )];
  }

  get groupsForFeature(): Group[] | T[] {
    let list: Group[] | T[];

    if (this.vgtManager) {
      list = this.vgtManager.results.data;
    } else if (this.groups !== null) {
      list = this.groups;
    } else {
      list = this.groupStore.groups.filter(e => e.feature === this.feature);
    }

    // Unified filter: Ensure we only return items where isPinned is true
    if (this.showPinnedGroupsOnly) {
      list = (list as T[]).filter((e: T) => (applyGroupSelector(e, this.groupSelector)).isPinned) as T[];
    }
    return list;
  }

  get groupsForFeatureById(): Map<string, Group | T> {
    //todo it's a bit dangerous to assume that T has an id prop
    return new Map((this.groupsForFeature as (Group | T)[]).map(e => [(e as { id: string }).id, e]));
  }

  get columnsConfig(): unknown[] {
    let columns: unknown[] = [
      {
        label: this.$t('txtName').toString(),
        field: typeof (this.groupSelector) === 'function'
            ? (e: T) => applyGroupSelector(e, this.groupSelector).name
            : [this.groupSelector, 'name'].filter(e => e.trim().length).join('.'),
      },
      {
        label: this.$t('group.model.createdOn').toString(),
        field: typeof (this.groupSelector) === 'function'
            ? (e: T) => applyGroupSelector(e, this.groupSelector).createdOn
            : [this.groupSelector, 'createdOn'].filter(e => e.trim().length).join('.'),
        type: 'date',
        formatFn: (date: Date) => formatDateTimeSeconds(date),
      },
      {
        label: this.$t('group.model.createdBy').toString(),
        field: typeof (this.groupSelector) === 'function'
            ? (e: T) => applyGroupSelector(e, this.groupSelector).createdByFullName
            : [this.groupSelector, 'createdByFullName'].filter(e => e.trim().length).join('.'),
      },
      {
        label: this.$t('group.model.users').toString(),
        field: typeof (this.groupSelector) === 'function'
            ? (e: T) => applyGroupSelector(e, this.groupSelector).userIds.length
            : [this.groupSelector, 'userIds.length'].filter(e => e.trim().length).join('.'),
        sortable: false,
      },
      {
        label: this.$t('group.model.usersExternal').toString(),
        field: typeof (this.groupSelector) === 'function'
            ? (e: T) => applyGroupSelector(e, this.groupSelector).externalUsers.length
            : [this.groupSelector, 'externalUsers.length'].filter(e => e.trim().length).join('.'),
        sortable: false,
      },
      {
        label: this.$t('txtActions').toString(),
        field: (e: T) => '',
        sortable: false,
        isActions: true,
      },
    ];

    if (this.vgtColumnModifier) {
      columns = this.vgtColumnModifier(columns);
    }
    return columns;
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get sortOptions(): any {
    return {
      enabled: true,
      initialSortBy: {
        field: this.columnsConfig.find(e => e.label === this.$t('group.model.createdOn').toString()).field,
        type: 'desc',
      },
    };
  }

  get usersAndUnknownUser(): ListItem[] {
    return [...this.users, { id: '00000000-0000-0000-0000-000000000000', firstName: '(' + this.$t('general.unknown') + ')' } as ListItem];
  }

  get usersById(): Map<string, ListItem> {
    return new Map(this.usersAndUnknownUser.map(e => [e.id!, e]));
  }

  mounted(): void {
    this.reloadAll();
  }

  private async groupDeleteFunctionInternal(group: Group): Promise<void> {
    await this.api.delete(group.id);
    this.groupStore.forgetGroup(group);
  }

  groupInitFunctionInternal(): Group {
    return Group.fromJS({
      id: emptyUUID(),
      name: '',
      fkAccountId: window.application.user.fkAccountId,
      createdOn: new Date(),
      createdBy: window.application.user.id,
      feature: this.feature,
      archived: false,
      archivedOn: null,
      archivedBy: null,
      userIds: [],
      externalUsers: [],
    });
  }

  private async reloadAll(): Promise<void> {
    if (this.vgtManager) {
      // await this.vgtManager.reload(); //not required here since the reload is triggered elsewhere
    } else {
      if (this.groups === null) { //only load plain groups if none were supplied from the outside
        await this.groupStore.reload();
      }
    }
    await this.reloadUsers();
  }

  private async reloadUsers(): Promise<void> {
    const list = await this.api.listUsers();
    this.users.splice(0);
    this.users.push(...list);
  }

  //events
  onAdd(): void {
    const group = this.groupInitFunction ? this.groupInitFunction() : this.groupInitFunctionInternal() as T;
    (this.$refs.editmodal as EditModal).open(group);
  }

  onEdit(group: T): void {
    const actualGroup = (this.groupsForFeature as any[]).find(e => e.id === (group as any).id); //find original object since vgt makes a deep copy
    (this.$refs.editmodal as EditModal).open(actualGroup);
  }

  async onDelete(group: T): Promise<void> {
    if (!window.confirm(this.$t('txtInfoDelete').toString())) {
      return;
    }

    try {
      await (this.groupDeleteFunction
          ? this.groupDeleteFunction(group)
          : this.groupDeleteFunctionInternal(group as Group));
      this.$toasted.info(this.$t('txtDeleted').toString());
    } catch (e) {
      handleError(this.$toasted, this.$i18n, e);
    }
  }
}
